<template>
  <div class="program-list">
    <div class="filters">
      <input
        v-model="filters.search"
        type="text"
        class="input-search"
        :placeholder="$t('bo.common.search')"
        @input="debouncedLoad"
      />
      <select v-model="filters.status" class="select-filter" @change="loadPrograms()">
        <option value="">{{ $t('bo.programs.allStatuses') }}</option>
        <option value="draft">{{ $t('bo.programs.statusDraft') }}</option>
        <option value="active">{{ $t('bo.programs.statusActive') }}</option>
        <option value="archived">{{ $t('bo.programs.statusArchived') }}</option>
      </select>
    </div>

    <div v-if="loading" class="state-msg">{{ $t('bo.common.loading') }}</div>
    <div v-else-if="error" class="state-msg error">{{ error }}</div>
    <div v-else-if="programs.length === 0" class="state-msg empty">
      <div class="empty-icon">📭</div>
      <div>{{ $t('bo.programs.noPrograms') }}</div>
    </div>

    <template v-else>
      <div class="list-meta">{{ $t('bo.programs.totalCount', { count: pagination.total }) }}</div>

      <table class="table">
        <thead>
          <tr>
            <th>{{ $t('bo.programs.colName') }}</th>
            <th>{{ $t('bo.programs.colStatus') }}</th>
            <th>{{ $t('bo.programs.colLocales') }}</th>
            <th>{{ $t('bo.programs.colUpdated') }}</th>
            <th>{{ $t('bo.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in programs" :key="row.id">
            <td class="col-name">
              <div class="row-title">{{ row.name || row.title }}</div>
              <div v-if="row.objective" class="row-meta">{{ row.objective }}</div>
            </td>
            <td>
              <span :class="['badge-status', 'st-' + row.status]">{{ statusLabel(row.status) }}</span>
            </td>
            <td class="col-locales">{{ localesLabel(row.available_locales) }}</td>
            <td class="col-date">{{ formatDate(row.updated_at) }}</td>
            <td class="actions">
              <button type="button" class="btn-action btn-edit" @click="$emit('edit', row)">
                {{ $t('bo.common.edit') }}
              </button>
              <button
                v-if="row.status !== 'archived'"
                type="button"
                class="btn-action btn-archive"
                @click="$emit('archive', row.id)"
              >
                {{ $t('bo.programs.archive') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="pagination.last_page > 1" class="pagination">
        <button
          type="button"
          class="page-btn"
          :disabled="pagination.current_page === 1"
          @click="loadPage(pagination.current_page - 1)"
        >
          {{ $t('bo.common.previous') }}
        </button>
        <span class="page-info">
          {{ $t('bo.translations.pageInfo', { current: pagination.current_page, last: pagination.last_page }) }}
        </span>
        <button
          type="button"
          class="page-btn"
          :disabled="pagination.current_page === pagination.last_page"
          @click="loadPage(pagination.current_page + 1)"
        >
          {{ $t('bo.common.next') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import adminProgramService from '@/services/adminProgramService'

const { t } = useI18n()

defineEmits(['edit', 'archive'])

const programs = ref([])
const loading = ref(false)
const error = ref('')
const pagination = ref({ current_page: 1, last_page: 1, total: 0 })
const filters = reactive({ search: '', status: '' })

let debounceTimer = null
const debouncedLoad = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => loadPrograms(), 300)
}

const loadPrograms = async (page = 1) => {
  loading.value = true
  error.value = ''
  try {
    const params = { page, ...filters }
    Object.keys(params).forEach((k) => params[k] === '' && delete params[k])
    const res = await adminProgramService.getAll(params)
    // AdminProgramController::index -> { data: <paginator> }
    const paginator = res.data?.data || {}
    programs.value = Array.isArray(paginator.data) ? paginator.data : []
    pagination.value = {
      current_page: paginator.current_page || 1,
      last_page: paginator.last_page || 1,
      total: paginator.total ?? programs.value.length,
    }
  } catch {
    error.value = t('bo.programs.failedLoad')
  } finally {
    loading.value = false
  }
}

const loadPage = (page) => loadPrograms(page)

const statusLabel = (status) =>
  ({
    draft: t('bo.programs.statusDraft'),
    ready: t('bo.programs.statusReady'),
    active: t('bo.programs.statusActive'),
    archived: t('bo.programs.statusArchived'),
    completed: t('bo.programs.statusCompleted'),
  })[status] || status

const localesLabel = (locales) => (Array.isArray(locales) && locales.length ? locales.join(', ').toUpperCase() : '—')

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

defineExpose({ loadPrograms })
onMounted(() => loadPrograms())
</script>

<style scoped>
.filters {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.input-search {
  flex: 1;
  min-width: 180px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.select-filter {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.state-msg {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.state-msg.error {
  color: #dc2626;
}

.empty-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.program-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.list-meta {
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.8rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.table tr:last-child td {
  border-bottom: none;
}

.col-name {
  max-width: 340px;
}

.col-date {
  white-space: nowrap;
  color: #6b7280;
  font-size: 0.8rem;
}

.col-locales {
  color: #6b7280;
  font-size: 0.8rem;
}

.row-title {
  font-weight: 600;
  color: #1f2937;
}

.row-meta {
  font-size: 0.78rem;
  color: #9ca3af;
  margin-top: 0.15rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
}

.badge-status {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.st-active {
  background: #d1fae5;
  color: #065f46;
}

.st-draft {
  background: #fef3c7;
  color: #92400e;
}

.st-archived {
  background: #f3f4f6;
  color: #6b7280;
}

.st-ready {
  background: #dbeafe;
  color: #1e40af;
}

.st-completed {
  background: #ede9fe;
  color: #5b21b6;
}

.actions {
  white-space: nowrap;
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.btn-action {
  padding: 0.3rem 0.65rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
}

.btn-edit {
  background: #e0e7ff;
  color: #3730a3;
}

.btn-edit:hover {
  background: #c7d2fe;
}

.btn-archive {
  background: #f3f4f6;
  color: #6b7280;
}

.btn-archive:hover {
  background: #e5e7eb;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.page-btn {
  padding: 0.4rem 0.9rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:hover:not(:disabled) {
  background: #f9fafb;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.875rem;
  color: #6b7280;
}
</style>
